import { Injectable } from "@angular/core";

import { combineLatest, Observable } from "rxjs";
import { first, map, mapTo, shareReplay } from "rxjs/operators";

import { DffCurrentRoute } from "@dffedb/router";
import { CookieStorage } from "@dffedb/util";
import { LoginService } from "@e-forsyning/common/auth";
import { Rolle } from "@e-forsyning/common/bruger";
import { AktuelBrugerObserver } from "@e-forsyning/common/bruger";
import { SilentLoginService } from "@e-forsyning/common/silent-login";

import { EBoksPopupService } from "../e-boks";
import { KontaktoplysningerPopup } from "./kontaktoplysninger-popup";
import { KontaktoplysningerPopupRef } from "./kontaktoplysninger-popup-ref";
import { KontaktoplysningerPopupRepository } from "./kontaktoplysninger-popup.repository";

@Injectable({ providedIn: "root" })
export class KontaktoplysningerPopupService {
    public popupAnnulleret: boolean;
    private readonly erForbruger$: Observable<boolean>;
    private readonly erKampagneAktiveret$: Observable<boolean>;

    constructor(
        private readonly popup: KontaktoplysningerPopup,
        private readonly repository: KontaktoplysningerPopupRepository,
        private readonly loginService: LoginService,
        private readonly cookieStorage: CookieStorage,
        private readonly eboksPopupService: EBoksPopupService,
        private readonly aktuelBrugerObserver: AktuelBrugerObserver,
        private readonly currentRoute: DffCurrentRoute,
        private readonly silentLoginService: SilentLoginService
    ) {
        this.erForbruger$ = this.erForbruger();
        this.erKampagneAktiveret$ = this.erKampagneAktiveret();
    }

    public get popupAlleredeVist(): boolean {
        return !!this.cookieStorage.get("kontaktoplysninger-popup-vist");
    }

    public set popupAlleredeVist(value: boolean) {
        // Vi viser popup'en i morgen hvis der er annuleret ellers om 90 dage
        this.cookieStorage.set("kontaktoplysninger-popup-vist", value.toString(), this.popupAnnulleret ? 1 : 90);

        // this.cookieStorage.set("kontaktoplysninger-popup-vist", value.toString(), 90);
    }

    public async aabnHvisIkkeAlleredeTilmeldt(): Promise<void> {
        if (await this.skalPopupVises()) {
            return this.open().closed.pipe(first(), mapTo(null)).toPromise();
        } else {
            return Promise.resolve();
        }
    }

    public async skalPopupVises() {
        if (this.visIkkePopupPaaAktuelleSide()) {
            return false;
        }
        if (this.silentLoginService.erLoggetPaaViaSilentLogin) {
            return false;
        }
        if (!this.loginService.erLoggetInd()) {
            return false;
        }
        if (this.popupAlleredeVist) {
            return false;
        }
        const forbrugerElKampagne = combineLatest([this.erForbruger$.pipe(first()), this.erKampagneAktiveret$.pipe(first())]);

        return Promise.resolve(forbrugerElKampagne);
    }

    private open(): KontaktoplysningerPopupRef {
        const result = this.popup.open();
        result.closed.pipe(first()).subscribe(() => {
            this.popupAnnulleret = false;
            this.popupAlleredeVist = true;
        });
        result.canceled.pipe(first()).subscribe(() => {
            this.popupAnnulleret = true;
            this.popupAlleredeVist = true;
        });
        return result;
    }

    private erKampagneAktiveret(): Observable<boolean> {
        return this.repository.hentIndstillinger().pipe(
            map((result) => result.visKampagne),
            shareReplay(1)
        );
    }

    private erForbruger(): Observable<boolean> {
        return this.aktuelBrugerObserver.valueChanges.pipe(map((bruger) => bruger && bruger.rolle === Rolle.Forbruger));
    }

    private visIkkePopupPaaAktuelleSide(): boolean {
        const ignoredRoutes = [/^\/login$/, /^\/login\//, /^\/silent-login\//];
        return ignoredRoutes.some((r) => r.test(this.currentRoute.value.path));
    }
}
